/* A7URO cinematic photography. Progressive enhancement; no content depends on animation. */
(() => {
  'use strict';
  if (window.A7Cinema) return;
  const instances = new Map();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = () => document.documentElement.classList.contains('shopify-design-mode');
  function create(hero) {
    const slides = [...hero.querySelectorAll('[data-cinema-slide]')];
    if (!slides.length) return () => {};
    const dots = [...hero.querySelectorAll('[data-cinema-go]')];
    const pause = hero.querySelector('[data-cinema-pause]');
    const caption = hero.querySelector('[data-cinema-caption]');
    const clean = [], animations = new Set();
    let current = 0, timer = 0, visible = true, disposed = false;
    let paused = hero.dataset.autoplay !== 'true' || reduce.matches || editor();
    const interval = Math.max(4, Math.min(20, Number(hero.dataset.interval) || 8)) * 1000;
    const duration = Math.max(300, Math.min(2400, Number(hero.dataset.transition) || 1400));
    const on = (node, event, fn, options) => { if (!node) return; node.addEventListener(event, fn, options); clean.push(() => node.removeEventListener(event, fn, options)); };
    const stopAnimations = () => { animations.forEach(a => a.cancel()); animations.clear(); slides.forEach(s => s.classList.remove('is-leaving')); };
    function animate(node, frames, timing) {
      if (!node?.animate || reduce.matches || editor() || hero.dataset.motion === 'false') return;
      try {
        const animation = node.animate(frames, {duration, easing:'cubic-bezier(.22,1,.36,1)', fill:'backwards', ...timing});
        animations.add(animation);
        animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
      } catch (_) { /* A failed animation must never hide an image or its content. */ }
    }
    function videos() {
      slides.forEach((slide,i) => slide.querySelectorAll('video').forEach(video => {
        const mobileOnly = video.classList.contains('a7-cinema__video-mobile');
        const desktopOnly = video.classList.contains('a7-cinema__video-desktop');
        const permitted = !(mobileOnly && innerWidth > 749) && !(desktopOnly && innerWidth <= 749);
        if (i === current && permitted && !paused && visible && !document.hidden && !reduce.matches) video.play()?.catch(() => {});
        else video.pause();
      }));
    }
    function schedule() {
      clearTimeout(timer);
      hero.classList.toggle('is-paused', paused);
      hero.classList.toggle('is-dormant', !visible || document.hidden);
      if (pause) {
        pause.textContent = paused ? (pause.dataset.playLabel || 'Play') : (pause.dataset.pauseLabel || 'Pause');
        pause.setAttribute('aria-pressed',String(paused));
      }
      videos();
      if (!paused && visible && !document.hidden && !reduce.matches && !disposed && slides.length > 1) timer = setTimeout(() => show(current+1, true),interval);
    }
    function sync() {
      slides.forEach((slide,i) => {
        const active = i === current;
        slide.classList.toggle('is-active',active);
        slide.setAttribute('aria-hidden',String(!active));
        slide.inert = !active;
      });
      dots.forEach((dot,i) => dot.setAttribute('aria-pressed',String(i === current)));
      if (caption) caption.textContent = slides[current].dataset.caption || '';
      hero.dataset.current = String(current);
      const nextImage = slides[(current+1)%slides.length].querySelector('img');
      if(nextImage) nextImage.loading = 'eager';
    }
    function show(index, automatic = false, play = true) {
      if (disposed) return;
      const next = (index + slides.length) % slides.length;
      if (next === current) { schedule(); return; }
      stopAnimations();
      const previous = slides[current];
      current = next;
      sync();
      if (play && !reduce.matches && !editor() && hero.dataset.motion !== 'false') {
        previous.classList.add('is-leaving');
        animate(previous,[{opacity:1},{opacity:0}],{duration:duration*.85,easing:'ease-in-out'});
        animate(slides[current],[{opacity:0},{opacity:1}],{easing:'cubic-bezier(.4,0,.2,1)'});
        const visual = slides[current].querySelector('.a7-cinema__visual');
        animate(visual,[{transform:hero.classList.contains('a7-cinema--full')?'scale(1.035)':'scale(.975)'},{transform:'scale(1)'}],{duration:duration+700});
        slides[current].querySelectorAll('[data-cinema-copy]').forEach((node,i) => {
          animate(node,[{opacity:0,transform:'translate3d(0,24px,0)'},{opacity:1,transform:'translate3d(0,0,0)'}],{duration:Math.min(duration,1100),delay:180+i*90});
        });
      }
      if (!automatic) hero.dataset.manual = 'true';
      schedule();
    }
    on(hero.querySelector('[data-cinema-prev]'),'click',()=>show(current-1));
    on(hero.querySelector('[data-cinema-next]'),'click',()=>show(current+1));
    dots.forEach((dot,i)=>on(dot,'click',()=>show(i)));
    on(pause,'click',()=>{paused=!paused;if(paused)stopAnimations();schedule();});
    on(hero,'keydown',event=>{
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName)) return;
      if(event.key==='ArrowRight'){event.preventDefault();show(current+1);}
      if(event.key==='ArrowLeft'){event.preventDefault();show(current-1);}
    });
    // Pause autonomous changes while a visitor is reading/focusing a slide's content.
    on(hero,'focusin',()=>{clearTimeout(timer);});
    on(hero,'focusout',event=>{if(!hero.contains(event.relatedTarget))schedule();});
    on(document,'visibilitychange',()=>{if(document.hidden)stopAnimations();schedule();});
    on(window,'resize',videos,{passive:true});
    on(reduce,'change',()=>{if(reduce.matches){paused=true;stopAnimations();}schedule();});
    on(hero.closest('.shopify-section') || hero,'shopify:block:select',event=>{
      const index = slides.findIndex(s=>s.dataset.blockId === event.detail?.blockId);
      if(index>=0){paused=true;show(index,false,false);}
    });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries=>{visible=entries[0].isIntersecting;schedule();});
      observer.observe(hero);clean.push(()=>observer.disconnect());
    }
    sync();schedule();
    slides[current].querySelectorAll('[data-cinema-copy]').forEach((node,i)=>animate(node,[{opacity:0,transform:'translate3d(0,22px,0)'},{opacity:1,transform:'translate3d(0,0,0)'}],{duration:1100,delay:140+i*90}));
    return ()=>{disposed=true;clearTimeout(timer);stopAnimations();clean.forEach(fn=>fn());slides.forEach(s=>s.querySelectorAll('video').forEach(v=>v.pause()));};
  }
  function mount(root=document) {
    const heroes = [...root.querySelectorAll('[data-cinema]')];
    if(root.matches?.('[data-cinema]'))heroes.unshift(root);
    heroes.forEach(hero=>{instances.get(hero)?.();instances.set(hero,create(hero));});
  }
  function unmount(root) {
    instances.forEach((dispose,hero)=>{if(root===hero||root.contains(hero)){dispose();instances.delete(hero);}});
  }
  window.A7Cinema = {mount,unmount};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>mount(),{once:true});else mount();
  document.addEventListener('shopify:section:load',event=>mount(event.target));
  document.addEventListener('shopify:section:unload',event=>unmount(event.target));
})();
